// WebSocket signaling client. Speaks the shared Lumi protocol and exposes a
// tiny typed event emitter. Auto-replies to server PINGs. ASCII only.

import { SERVER_WS } from './config.js';
import { ClientMsg, ServerMsg, HEARTBEAT_INTERVAL_MS } from '@lumi/shared';

// A dead server does not always close the socket: a killed process, a dropped
// mobile network, or a sleeping proxy can leave it HALF-OPEN, where onclose
// never fires and the page believes it is still connected. The server pings on
// a known interval, so silence past a few missed pings means the connection is
// gone -- and a chat must never outlive the server's knowledge of it.
const SERVER_SILENCE_MS = HEARTBEAT_INTERVAL_MS * 3;

export class Signaling {
  constructor() {
    this.ws = null;
    this.handlers = new Map(); // type -> Set<fn>
    this.closed = false;
    this.watchdog = null;
    this.lastSeen = 0;
    // Bumped by every connect(). A socket carries the number it was opened
    // with, and anything it says while holding a stale one is discarded --
    // otherwise the old socket's onclose (which arrives AFTER its replacement
    // is already open, and after a failed attempt too) would announce a
    // disconnection that has already been recovered from, and the page would
    // tear down a working connection.
    this.generation = 0;
  }

  /** Treat prolonged server silence as a disconnect. */
  _armWatchdog(gen) {
    clearInterval(this.watchdog);
    this.lastSeen = Date.now();
    this.watchdog = setInterval(() => {
      if (this.closed || gen !== this.generation) {
        clearInterval(this.watchdog);
        this.watchdog = null;
        return;
      }
      if (Date.now() - this.lastSeen < SERVER_SILENCE_MS) return;
      clearInterval(this.watchdog);
      this.watchdog = null;
      try {
        this.ws?.close();
      } catch {
        /* already gone */
      }
      this._emit('__close', { reason: 'silent' });
    }, 5000);
  }

  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }

  _emit(type, payload) {
    this.handlers.get(type)?.forEach((fn) => {
      try {
        fn(payload);
      } catch (err) {
         
        console.error('handler error', type, err);
      }
    });
  }

  /**
   * Open a socket. Safe to call again on the same instance to REPLACE a dead
   * one: the handlers registered with on() survive, so a reconnect does not
   * have to rewire the whole protocol. The token is not reusable -- it lives
   * two minutes -- so a caller reconnecting must mint a fresh one.
   */
  connect(token) {
    return new Promise((resolve, reject) => {
      const gen = (this.generation += 1);
      const url = `${SERVER_WS}/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      this.ws = ws;
      ws.onopen = () => {
        this._armWatchdog(gen);
        resolve();
      };
      ws.onerror = () => reject(new Error('Could not connect to Lumi.'));
      ws.onclose = () => {
        if (gen !== this.generation) return; // already replaced; not our news
        clearInterval(this.watchdog);
        this.watchdog = null;
        if (!this.closed) this._emit('__close', {});
      };
      ws.onmessage = (ev) => {
        if (gen !== this.generation) return;
        this.lastSeen = Date.now(); // any traffic proves the server is alive
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.t === ServerMsg.PING) {
          this.send(ClientMsg.PONG, {});
          return;
        }
        this._emit(msg.t, msg);
      };
    });
  }

  send(type, payload = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: type, ...payload }));
    }
  }

  close() {
    this.closed = true;
    clearInterval(this.watchdog);
    this.watchdog = null;
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
  }
}
